import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useLanguage } from '@/contexts/LanguageContext';
import type { RunMetadataDto } from '@/api/control/missionImpactAPI.schemas';

interface SchemaChangesDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Handler for dialog open/close state changes */
  onOpenChange: (open: boolean) => void;
  /** The run metadata containing schema change details */
  run: RunMetadataDto | null;
}

/**
 * Dialog component that displays schema changes recorded during the prepare step.
 * Shows created columns, widened columns, and (on failure) rejected migration attempts.
 */
export function SchemaChangesDialog({ open, onOpenChange, run }: SchemaChangesDialogProps) {
  const { t } = useLanguage();
  const sc = t.allRuns.schemaChanges;

  const columnsCreated = run?.schemaChanges?.columnsCreated ?? [];
  const columnsWidened = run?.schemaChanges?.columnsWidened ?? [];
  const failedMigrations = run?.schemaChanges?.failedMigrations ?? [];
  const hasAny = columnsCreated.length > 0 || columnsWidened.length > 0 || failedMigrations.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>{sc.title}</DialogTitle>
          <DialogDescription>{run?.runId}</DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 space-y-4 pr-1">
          {!hasAny && (
            <p className="text-sm text-muted-foreground py-4">{sc.noChanges}</p>
          )}

          {columnsCreated.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">{sc.columnsCreated}</h4>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{sc.entity}</TableHead>
                      <TableHead>{sc.columnName}</TableHead>
                      <TableHead>{sc.sqlType}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {columnsCreated.map((col) => (
                      <TableRow key={`${col.entity ?? ''}/${col.columnName ?? ''}`}>
                        <TableCell className="font-mono text-xs">{col.entity}</TableCell>
                        <TableCell className="font-mono text-xs">{col.columnName}</TableCell>
                        <TableCell className="font-mono text-xs">{col.sqlType}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {columnsWidened.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">{sc.columnsWidened}</h4>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{sc.entity}</TableHead>
                      <TableHead>{sc.columnName}</TableHead>
                      <TableHead>{sc.fromType}</TableHead>
                      <TableHead>{sc.toType}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {columnsWidened.map((col) => (
                      <TableRow key={`${col.entity ?? ''}/${col.columnName ?? ''}`}>
                        <TableCell className="font-mono text-xs">{col.entity}</TableCell>
                        <TableCell className="font-mono text-xs">{col.columnName}</TableCell>
                        <TableCell className="font-mono text-xs">{col.fromType}</TableCell>
                        <TableCell className="font-mono text-xs">{col.toType}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {failedMigrations.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-destructive">{sc.failedMigrations}</h4>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{sc.columnName}</TableHead>
                      <TableHead>{sc.fromType}</TableHead>
                      <TableHead>{sc.toType}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {failedMigrations.map((col) => (
                      <TableRow key={col.col} className="text-destructive">
                        <TableCell className="font-mono text-xs">{col.col}</TableCell>
                        <TableCell className="font-mono text-xs">{col.from}</TableCell>
                        <TableCell className="font-mono text-xs">{col.to}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end shrink-0 pt-2">
          <Button variant="outline" onClick={() => { onOpenChange(false); }}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
